// SetupCallModal — "Book a 15-minute setup call".
//
// The only place Parcely asks for a phone number (founder decision,
// 2026-10-20). The number is optional; if given, the user must tick a box
// whose wording (SETUP_CALL_CONSENT_TEXT) limits it to scheduling and holding
// this call. The server stores that exact wording and the time with the
// number (setup_call_requests, migration 246) and rejects a number without
// it. Keep the text identical to SetupCallConsentText in
// backend/internal/handlers/setup_call_handler.go.
//
// Calls happen outside 9-5 ET on weekdays (the founder works a day job), so
// the form only offers weekday evenings and weekends. "any" means either of
// those; the older weekday_morning/afternoon codes stay valid server-side so
// existing rows keep passing the CHECK in migration 246.
//
// Opened by the Dashboard for /setup-call?source=<where> (the day-0 and
// win-back emails link there) and from the user menu.

import React, { useEffect, useState } from "react";
import { capture } from "../lib/analytics";
import { postSetupCallRequest, SetupCallBestTime } from "../api";
import { useFocusTrap } from "../hooks/useFocusTrap";

// The founder's Google Calendar booking page (weekday evenings and weekends
// only). When set, the modal sends people there to pick a slot themselves and
// keeps the request form as a fallback; Google Calendar then holds the booking
// and emails both sides. Empty = request form only.
export const SETUP_CALL_BOOKING_URL = "https://calendar.app.google/EmwXZxGuZVvijaaw9";

export const SETUP_CALL_CONSENT_TEXT =
  "Parcely may call or text this number only to schedule and hold the setup call I requested. It will not be used for marketing.";

const BEST_TIMES: ReadonlyArray<{ code: SetupCallBestTime; label: string }> = [
  { code: "weekday_evening", label: "Weekday evening, after 6 pm ET" },
  { code: "weekend", label: "Saturday or Sunday" },
  { code: "any", label: "Either works" },
];

interface SetupCallModalProps {
  /** Where the request came from: day0_email, winback_email, user_menu, ... */
  source: string;
  onClose: () => void;
  /** Overrides SETUP_CALL_BOOKING_URL (tests). */
  bookingUrl?: string;
}

const SetupCallModal: React.FC<SetupCallModalProps> = ({ source, onClose, bookingUrl = SETUP_CALL_BOOKING_URL }) => {
  const [showForm, setShowForm] = useState(!bookingUrl);
  const [bestTime, setBestTime] = useState<SetupCallBestTime>("any");
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const trapRef = useFocusTrap<HTMLDivElement>({ onEscape: onClose });

  useEffect(() => {
    capture("setup_call_viewed", { source, booking_link: !!bookingUrl });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasPhone = phone.trim().length > 0;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (hasPhone && !consent) {
      setError("Tick the box to let us use the number for this call, or leave the number blank.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await postSetupCallRequest({ phone: phone.trim(), phoneConsent: hasPhone && consent, bestTime, note: note.trim(), source });
      setSent(true);
    } catch (err: any) {
      setError(err?.response?.data?.message || "Couldn't send that. Please try again, or reply to any Parcely email instead.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-backdrop" onClick={onClose}>
      <div
        className="upgrade-modal setup-call-modal"
        onClick={(e) => e.stopPropagation()}
        ref={trapRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="setup-call-title"
      >
        <button className="auth-close" onClick={onClose} aria-label="Close">
          ×
        </button>
        <h2 className="upgrade-title" id="setup-call-title">
          {sent ? "Thanks, request sent" : "Book a 15-minute setup call"}
        </h2>
        {sent ? (
          <>
            <p className="upgrade-reason">
              I'll email you within one business day to pick a time. Bring the county and the kind of deal you are looking for, and
              I'll show you how to find them in Parcely.
            </p>
            <div className="upgrade-actions">
              <button type="button" className="upgrade-btn-primary" onClick={onClose}>
                Done
              </button>
            </div>
          </>
        ) : !showForm ? (
          <>
            <p className="upgrade-reason">
              A quick call with the founder: what you're trying to buy, which counties, and how to set Parcely up for it. No sales pitch.
              Calls are on weekday evenings or weekends.
            </p>
            <div className="upgrade-actions">
              <button type="button" className="upgrade-btn-secondary" onClick={onClose}>
                Not now
              </button>
              <a
                className="upgrade-btn-primary setup-call-book"
                href={bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => capture("setup_call_booking_opened", { source })}
              >
                Pick a time
              </a>
            </div>
            <button
              type="button"
              className="setup-call-fallback"
              onClick={() => {
                capture("setup_call_form_fallback", { source });
                setShowForm(true);
              }}
            >
              No time works? Send a request and I'll email you.
            </button>
          </>
        ) : (
          <form onSubmit={submit} className="setup-call-form">
            <p className="upgrade-reason">
              A quick call with the founder: what you're trying to buy, which counties, and how to set Parcely up for it. No sales pitch.
              Calls are on weekday evenings or weekends.
            </p>

            <fieldset className="setup-call-fieldset">
              <legend className="attribution-other-label">When suits you?</legend>
              <div className="attribution-choices">
                {BEST_TIMES.map((t) => (
                  <button
                    key={t.code}
                    type="button"
                    aria-pressed={bestTime === t.code}
                    className={`attribution-chip${bestTime === t.code ? " attribution-chip--active" : ""}`}
                    onClick={() => setBestTime(t.code)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </fieldset>

            <label htmlFor="setup-call-phone" className="attribution-other-label">
              Phone (optional; we can do the call by video instead)
            </label>
            <input
              id="setup-call-phone"
              className="county-picker-search"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(704) 555-0134"
            />
            {hasPhone && (
              <label className="setup-call-consent">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
                <span>{SETUP_CALL_CONSENT_TEXT}</span>
              </label>
            )}

            <label htmlFor="setup-call-note" className="attribution-other-label">
              Anything I should know first? (optional)
            </label>
            <textarea
              id="setup-call-note"
              className="county-picker-search setup-call-note"
              rows={3}
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. I wholesale in Gaston and Lincoln and want owner contacts"
            />

            {error && (
              <p className="county-picker-error" role="alert">
                {error}
              </p>
            )}
            <div className="upgrade-actions">
              <button type="button" className="upgrade-btn-secondary" onClick={onClose}>
                Not now
              </button>
              <button type="submit" className="upgrade-btn-primary" disabled={busy}>
                {busy ? "Sending…" : "Request a call"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default SetupCallModal;
